import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import useStore from 'state/knovStore'
import styles from 'components/quests/ai-magic-wand-menu-button.module.scss'
import cn from 'classnames'
import WithTooltip from 'components/shared/WithTooltip'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { Info, Image as ImageIcon, Video as VideoIcon, Music as MusicIcon } from 'lucide-react'
import { IAiModel, AiModelUIDefault } from 'state/aiModels'

// type combining ai model and ui defaults
type MenuItemType = (IAiModel | AiModelUIDefault) & {
    usdTreechatPublicPrice?: number
    usdTreechatPrivatePrice?: number
}

// Define the reusable Trigger Button structure component here
interface AiTriggerButtonProps {
    icon: React.ReactNode
    hover: boolean
    contextStyles?: string
    onClick?: (e?: React.MouseEvent) => void
    onMouseEnter?: () => void
    onMouseLeave?: () => void
}
const AiTriggerButton = React.forwardRef<HTMLDivElement, AiTriggerButtonProps>(
    ({ icon, hover, contextStyles, onClick, onMouseEnter, onMouseLeave }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    styles.aiMagicWandMenuButtonUIMainComp,
                    contextStyles,
                    hover && styles.hover,
                )}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <div className={cn(styles.toggleAiMagicWandMenuButton)}>{icon}</div>
                <div className={cn(styles.separator, hover && styles.activeSeparator)} />
                <div className={cn(styles.aiMagicWandMenuButtonModel)}>
                    <i className="fa fa-ellipsis-v" />
                </div>
            </div>
        )
    },
)

interface AiPopupMenuBaseProps {
    // triggerButton prop now just expects the Icon element
    triggerButtonIcon: React.ReactElement
    items: MenuItemType[]
    getEditor: () => any
    questIsPublic: boolean
    tooltipText: string
    menuContainerClassName?: string
    contextStyles?: string // Add back contextStyles
    menuTitle?: string // new prop for menu title
    menuIcon?: React.ReactElement // new prop for menu icon
    showModelTypeIcon?: boolean // new prop to control model type icon visibility in menu items
}

// shared component to handle info icon and tooltip state
export function MenuItemWithInfo({
    item,
    isActive,
    getEditor,
    setShowMenu,
    isPublic,
    showModelTypeIcon, // new prop
}: {
    item: MenuItemType
    isActive: boolean
    getEditor: () => any
    setShowMenu: (show: boolean) => void
    isPublic: boolean
    showModelTypeIcon?: boolean
}) {
    const tooltipRef = React.useRef<any>(null)
    const isProcessingRef = React.useRef(false) // Add click guard ref
    const price = isPublic ? item.usdTreechatPublicPrice : item.usdTreechatPrivatePrice
    let priceDisplay = null
    if (typeof price === 'number' && price > 0) {
        if (price >= 0.01) {
            priceDisplay = (
                <>
                    {Math.round(price * 100)}
                    <span
                        style={{
                            fontSize: '0.85em',
                            verticalAlign: 'super',
                            marginLeft: '1px',
                        }}
                    >
                        ¢
                    </span>
                </>
            )
        } else {
            priceDisplay = (
                <>
                    {(price * 100).toFixed(1)}
                    <span
                        style={{
                            fontSize: '0.85em',
                            verticalAlign: 'super',
                            marginLeft: '1px',
                        }}
                    >
                        ¢
                    </span>
                </>
            )
        }
    }
    return (
        <WithTooltip tip={item.description} key={item.actionTagName} ref={tooltipRef}>
            <div
                className={cn(
                    styles.aiMagicWandMenuButtonMenuItem, // Use original style
                    isActive && styles.activeModel,
                )}
                onClick={() => {
                    const editor = getEditor()
                    if (!editor) return

                    const actionTag = `!${item.actionTagName}`

                    // Check if this is a Tiptap editor (has commands property)
                    if (editor.commands) {
                        // Tiptap editor
                        try {
                            // Get the actual editor instance if it's wrapped
                            const actualEditor = editor.getEditor ? editor.getEditor() : editor

                            // Focus the editor first to ensure it's ready
                            if (actualEditor?.commands?.focus) {
                                // Focus at the end of the document for iOS compatibility
                                actualEditor.commands.focus('end')
                            }

                            // Small delay to ensure focus is established
                            setTimeout(() => {
                                try {
                                    const editorInstance = editor.getEditor
                                        ? editor.getEditor()
                                        : editor

                                    // Check if editor is available
                                    if (!editorInstance) {
                                        console.error('Editor instance not available')
                                        return
                                    }

                                    // For iOS, we need to ensure the editor is focused and has a valid selection
                                    // If there's no selection or the editor isn't focused, set cursor to end
                                    if (
                                        !editorInstance.isFocused ||
                                        !editorInstance.state?.selection
                                    ) {
                                        editorInstance.commands.focus('end')
                                    }

                                    // Get the current selection to check text before cursor
                                    const { $from } = editorInstance.state.selection
                                    const pos = $from.pos
                                    let addSpaceBefore = false

                                    // Check if there's non-whitespace character immediately before cursor
                                    if (pos > 0) {
                                        const textBefore = editorInstance.state.doc.textBetween(
                                            Math.max(0, pos - 1),
                                            pos,
                                        )
                                        if (textBefore && textBefore.trim().length > 0) {
                                            addSpaceBefore = true
                                        }
                                    }

                                    // Build content to insert
                                    const contentToInsert = []
                                    if (addSpaceBefore) {
                                        contentToInsert.push({
                                            type: 'text',
                                            text: ' ',
                                        })
                                    }
                                    contentToInsert.push(
                                        {
                                            type: 'text',
                                            text: actionTag,
                                            marks: [{ type: 'bold' }],
                                        },
                                        {
                                            type: 'text',
                                            text: ' ',
                                        },
                                    )

                                    // Insert the action tag with appropriate spacing
                                    editorInstance.commands.insertContent(contentToInsert)

                                    // No need to manually set cursor position - insertContent handles it
                                } catch (error) {
                                    console.error('Failed to insert text in Tiptap:', error)
                                    // Fallback: insert at end without selection change
                                    try {
                                        const editorInstance = editor.getEditor
                                            ? editor.getEditor()
                                            : editor
                                        if (editorInstance?.commands?.insertContent) {
                                            // Focus at end first
                                            editorInstance.commands.focus('end')
                                            // Then insert
                                            editorInstance.commands.insertContent(`${actionTag} `)
                                        }
                                    } catch (fallbackError) {
                                        console.error(
                                            'Fallback insertion also failed:',
                                            fallbackError,
                                        )
                                    }
                                }
                            }, 100) // Increased delay for iOS
                        } catch (error) {
                            console.error('Failed to focus editor:', error)
                        }
                    } else {
                        // Quill editor (original logic)
                        const cursorPosition = editor?.selection?.savedRange?.index ?? 0
                        try {
                            editor.insertText(cursorPosition, `${actionTag} `, 'bold', true, 'user')
                            editor.setSelection(cursorPosition + actionTag.length + 1)
                        } catch (error) {
                            console.error('Failed to insert text in Quill:', error)
                            const currentLength = editor.getLength()
                            editor.insertText(currentLength, `${actionTag} `, 'bold', true, 'user')
                            editor.setSelection(currentLength + actionTag.length + 1)
                        }
                    }

                    setShowMenu(false)
                }}
            >
                <span
                    className={styles.menuItemText}
                    style={{ display: 'flex', alignItems: 'center' }}
                >
                    {showModelTypeIcon && 'modelType' in item && item.modelType === 'image' && (
                        <ImageIcon size={13} style={{ marginRight: '6px', flexShrink: 0 }} />
                    )}
                    {showModelTypeIcon && 'modelType' in item && item.modelType === 'video' && (
                        <VideoIcon size={13} style={{ marginRight: '6px', flexShrink: 0 }} />
                    )}
                    {showModelTypeIcon && 'modelType' in item && item.modelType === 'audio' && (
                        <MusicIcon size={13} style={{ marginRight: '6px', flexShrink: 0 }} />
                    )}
                    <span style={{ whiteSpace: 'normal', textAlign: 'left' }}>
                        {item.humanName}
                    </span>
                    <span
                        className={styles.infoIcon}
                        onClick={e => {
                            e.stopPropagation()
                            tooltipRef.current?.showTooltip()
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label="Show info"
                    >
                        <Info size={13} />
                    </span>
                </span>
                {priceDisplay && <span className={styles.modelPrice}>{priceDisplay}</span>}
            </div>
        </WithTooltip>
    )
}

export default function AiPopupMenuBase({
    triggerButtonIcon, // Renamed prop
    items,
    getEditor,
    questIsPublic,
    tooltipText,
    menuContainerClassName,
    contextStyles,
    menuTitle,
    menuIcon,
    showModelTypeIcon,
}: AiPopupMenuBaseProps) {
    const { panelId } = usePanelContext()
    const [showMenu, setShowMenu] = useState(false)
    const buttonContainerRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<Element | null>(null)
    const SAFETY_PADDING = 60 // Increased padding to accommodate header
    const [hover, setHover] = useState(false)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})

    // Effect to find the panel container for portal rendering
    useEffect(() => {
        if (showMenu && panelId) {
            // attempt to find the panel element when the menu is shown and panelId is available
            panelRef.current = document.querySelector(`[data-panel-id="${panelId}"]`)
        } else {
            // if the menu is not shown or panelId is missing, set panelRef to null
            panelRef.current = null
        }
    }, [showMenu, panelId]) // re-run when showMenu or panelId changes

    const toggleMenu = (e?: React.MouseEvent) => {
        e?.stopPropagation()
        setShowMenu(prev => !prev)
    }

    // Effect to calculate menu position and max height
    useEffect(() => {
        if (showMenu && buttonContainerRef.current && panelRef.current) {
            const panelRect = panelRef.current.getBoundingClientRect()
            const buttonRect = buttonContainerRef.current.getBoundingClientRect()

            // adjust max height calculation if header is present
            const headerHeight = menuTitle ? 40 : 0 // estimate header height
            const availableSpaceAboveButton =
                buttonRect.top - panelRect.top - SAFETY_PADDING - headerHeight
            const currentMenuMaxHeight = Math.max(100, availableSpaceAboveButton)

            // Position: Horizontally centered in panel, vertically just above the button.
            // 'bottom' is the offset from the panel's bottom edge to where the menu's bottom should be.
            const menuBottomStyleValue = panelRect.height - (buttonRect.top - panelRect.top) + 5 // 5px padding above button's top

            setMenuStyle({
                position: 'absolute',
                left: '50%',
                bottom: `${menuBottomStyleValue}px`,
                transform: 'translateX(-50%)', // Only horizontal centering
                maxHeight: `${currentMenuMaxHeight}px`,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                zIndex: 1000,
            })
        } else if (!showMenu) {
            setMenuStyle({}) // Clear styles when menu is hidden
        }
    }, [showMenu, panelId, SAFETY_PADDING, menuTitle]) // Re-calculate if showMenu, panelId, or padding changes

    useEffect(() => {
        if (!showMenu) return

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonContainerRef.current &&
                !buttonContainerRef.current.contains(event.target as Node)
            ) {
                setShowMenu(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [showMenu])

    const renderMenuItems = () => {
        const getPrice = (item: MenuItemType) => {
            const price = questIsPublic ? item.usdTreechatPublicPrice : item.usdTreechatPrivatePrice
            return typeof price === 'number' && price > 0 ? price : 0
        }

        const sortedItems = [...items].sort((a, b) => {
            const priceA = getPrice(a)
            const priceB = getPrice(b)
            if (priceA === 0 && priceB > 0) return -1
            if (priceB === 0 && priceA > 0) return 1
            return priceA - priceB
        })

        return sortedItems.map(itemInfo => (
            <MenuItemWithInfo
                key={itemInfo.actionTagName}
                item={itemInfo}
                isActive={false}
                getEditor={getEditor}
                setShowMenu={setShowMenu}
                isPublic={questIsPublic}
                showModelTypeIcon={showModelTypeIcon}
            />
        ))
    }

    const renderMenu = () => {
        if (!showMenu || items.length === 0 || !panelRef.current) return null

        return createPortal(
            <div
                ref={menuRef}
                className={cn(styles.aiMagicWandMenuButtonMenu, menuContainerClassName)}
                style={menuStyle} // Apply dynamic style here
                onClick={e => e.stopPropagation()}
            >
                {menuTitle && menuIcon && (
                    <div className={styles.aiMagicWandMenuButtonHeader}>
                        {React.cloneElement(menuIcon, {
                            size: 24,
                            className: styles.menuHeaderIcon,
                        })}
                        <span className={styles.menuHeaderText}>{menuTitle}</span>
                    </div>
                )}
                {items.length > 0 ? (
                    renderMenuItems()
                ) : (
                    <div className={styles.aiMagicWandMenuEmpty}>No tools available</div>
                )}
            </div>,
            panelRef.current,
        )
    }

    return (
        <div className={styles.aiMagicWandMenuButtonContainer}>
            <WithTooltip tip={tooltipText}>
                <AiTriggerButton
                    ref={buttonContainerRef}
                    icon={triggerButtonIcon}
                    hover={hover}
                    contextStyles={contextStyles}
                    onClick={toggleMenu}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                />
            </WithTooltip>
            {renderMenu()}
        </div>
    )
}
